
import React, { useState, useEffect } from 'react';
import { Collapse, Form, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';

// TimeSelect Component with 12-hour display but 24-hour storage
const TimeSelect = ({ value, onChange, step = 5, className, disabled }) => {
  const toLabel = (h, m) => {
    const hour12 = ((h + 11) % 12) + 1; // 0 -> 12
    const mm = m.toString().padStart(2, '0');
    const ampm = h < 12 ? 'AM' : 'PM';
    return `${hour12}:${mm} ${ampm}`;
  };

  const toValue = (h, m) => {
    const hh = h.toString().padStart(2, '0');
    const mm = m.toString().padStart(2, '0');
    return `${hh}:${mm}:00`;
  };

  const normalize = (val) => {
    if (!val) return '';
    const parts = val.split(':');
    const hh = (parts[0] ?? '00').padStart(2, '0');
    const mm = (parts[1] ?? '00').padStart(2, '0');
    return `${hh}:${mm}:00`;
  };

  const options = React.useMemo(() => {
    const out = [];
    for (let minutes = 0; minutes < 24 * 60; minutes += step) {
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      out.push({ value: toValue(h, m), label: toLabel(h, m) });
    }
    return out;
  }, [step]);

  const normalized = normalize(value);

  return (
    <Form.Select
      value={normalized || ''}
      onChange={(e) => onChange(e.target.value)}
      className={className}
      disabled={disabled}
    >
      <option value="">Select time</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </Form.Select>
  );
};

const TeamEventAccordion = ({
  teamId,
  show,
  setShow,
  eventData = {},
  onEventDataChange,
  teamMembers = [],
}) => {
  const [expanded, setExpanded] = useState(false);
  const [sendInvite, setSendInvite] = useState(eventData.add_to_calendar || false);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    if (selectAll) {
      onEventDataChange(prev => ({
        ...prev,
        event_attendees: teamMembers.map(member => member.id)
      }));
    }
  }, [selectAll, teamMembers, onEventDataChange]);

    useEffect(() => {
        if(sendInvite === false && ((eventData?.event_attendees !== undefined && eventData.event_attendees.length > 0) || (eventData?.custom_attendees !== undefined && eventData.custom_attendees.length > 0))) {
            setSendInvite(true)
            onEventDataChange(prev => ({ ...prev, add_to_calendar: true }));
        }
    }, []);

  const handleMemberToggle = (id) => {
    onEventDataChange(prev => {
      const exists = prev.event_attendees?.includes(id);
      return {
        ...prev,
        event_attendees: exists
          ? prev.event_attendees.filter(mid => mid !== id)
          : [...(prev.event_attendees || []), id],
      };
    });
  };

  const calendarTooltip = (
    <Tooltip id="calendar-tooltip">
      An email with a calendar invite (.ics file) will be sent to the attendees. They can add it to their calendar apps.
    </Tooltip>
  );

    const handleStartDateChange = (value) => {
        onEventDataChange(prev => {
            const newStart = value;
            const shouldMirrorEnd =
                !prev.end_date || prev.end_date === prev.start_date;
            // mirror only if end is blank OR was mirroring the old start

            return {
                ...prev,
                start_date: newStart,
                end_date: shouldMirrorEnd ? newStart : prev.end_date,
            };
        });
    };

    const handleEndDateChange = (value) => {
        onEventDataChange(prev => ({
            ...prev,
            end_date: value, // explicit user edit; keep whatever they choose
        }));
    };

  return (
    <>
      <Button
        variant="success"
        size="sm"
        onClick={() => setShow(!show)}
        aria-expanded={show}
        className="btn-teal mb-2"
      >
        {show ? 'Hide Date Fields' : 'Add Date'}
      </Button>

      <Collapse in={show}>
        <div className="border p-3 mb-3 bg-light">
          <Form.Check
            type="checkbox"
            label={
              <>
                Add to attendees’ calendars (email invite)
                <OverlayTrigger placement="top" overlay={calendarTooltip}>
                  <span className="ml-1 text-info" style={{ cursor: 'pointer' }}>ⓘ</span>
                </OverlayTrigger>
              </>
            }
            checked={sendInvite}
            onChange={(e) => {
              setSendInvite(e.target.checked);
              onEventDataChange(prev => ({ ...prev, add_to_calendar: e.target.checked }));
            }}
          />

          <div className="d-flex gap-2 mt-3">
          <Form.Group className="flex-fill">
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              value={eventData.start_date || ''}
              onChange={(e) => handleStartDateChange(e.target.value)}
            />
          </Form.Group>

                     {!eventData.is_all_day && (
             <Form.Group className="flex-fill">
               <Form.Label>Start Time</Form.Label>
               <TimeSelect
                 value={eventData.start_time || ''}
                 onChange={(val) =>
                   onEventDataChange(prev => ({ ...prev, start_time: val }))
                 }
               />
             </Form.Group>
           )}
          </div>

          <div className="d-flex gap-2 mt-3">
            <Form.Group className="flex-fill">
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="date"
                value={eventData.end_date || ''}
                onChange={(e) => handleEndDateChange(e.target.value)}
              />
            </Form.Group>

                         {!eventData.is_all_day && (
               <Form.Group className="flex-fill">
                 <Form.Label>End Time</Form.Label>
                 <TimeSelect
                   value={eventData.end_time || ''}
                   onChange={(val) =>
                     onEventDataChange(prev => ({ ...prev, end_time: val }))
                   }
                 />
               </Form.Group>
             )}
          </div>

          <Form.Check
            type="checkbox"
            label="All-day event"
            className="mt-2"
            checked={eventData.is_all_day || false}
            onChange={(e) =>
              onEventDataChange(prev => ({ ...prev, is_all_day: e.target.checked }))
            }
          />

          <Form.Group className="mt-3">
            <Form.Label>Location</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter location"
              value={eventData.event_location || ''}
              onChange={(e) =>
                onEventDataChange(prev => ({ ...prev, event_location: e.target.value }))
              }
            />
          </Form.Group>

          {sendInvite && (
            <>
              <hr />
              <strong>Invite Team Members</strong>
              <Form.Check
                type="checkbox"
                label="Select All Team Members"
                checked={selectAll}
                onChange={(e) => setSelectAll(e.target.checked)}
                className="mb-2"
              />
              {teamMembers.map((member) => (
                <Form.Check
                  key={member.id}
                  type="checkbox"
                  label={member.KnownAs || member.name}
                  checked={eventData.event_attendees?.includes(member.id) || false}
                  onChange={() => handleMemberToggle(member.id)}
                />
              ))}

              <Form.Group className="mt-3">
                <Form.Label>
                  Invite Attendees not on Team
                  <small className="d-block text-muted">(comma-separated emails)</small>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="jdoe@example.com, team@example.org"
                  value={eventData.custom_attendees || ''}
                  onChange={(e) =>
                    onEventDataChange(prev => ({ ...prev, custom_attendees: e.target.value }))
                  }
                />
              </Form.Group>
            </>
          )}
        </div>
      </Collapse>
    </>
  );
};

export default TeamEventAccordion;
